import type { Animated, CropId, EnemyKind, Vec2 } from "./types";

export const CLIPS = {
  idle: { frames: 8, fps: 8, loop: true },
  walk: { frames: 16, fps: 16, loop: true },
  attack: { frames: 8, fps: 20, loop: false },
  gather: { frames: 8, fps: 20, loop: false },
  hurt: { frames: 8, fps: 24, loop: false },
  death: { frames: 8, fps: 12, loop: false },
  wind: { frames: 8, fps: 8, loop: true },
  harvest: { frames: 8, fps: 12, loop: false },
  water: { frames: 8, fps: 16, loop: false },
  plant: { frames: 8, fps: 16, loop: false },
  cast: { frames: 8, fps: 20, loop: false },
  dash: { frames: 8, fps: 24, loop: false },
} as const;
export type Clip = keyof typeof CLIPS;
export const CELL = 160;
export const PIVOT = { x: 80, y: 148 };
export const ANIMATION_VERSION = 5;
export const HERO_CLIPS = ["water", "plant", "cast", "dash"] as const;

/** 主角专用动作不扩充伙伴图集；此契约同时供导出器和运行时选择行。 */
export function atlasClips(id: string, environment = false): Clip[] {
  if (environment) return ["wind", "hurt", "harvest"];
  const rows: Clip[] = ["idle", "walk", "attack", "gather", "hurt", "death"];
  return id === "hero" ? [...rows, ...HERO_CLIPS] : rows;
}

/** 起手、命中、收势使用各自曲线；采集是抬起再下劈，不复用攻击摆臂。 */
export const ACTION_PHASES = {
  attack: {
    arm: [-0.12, -0.3, -0.62, 0.5, 1.05, 0.7, 0.25, 0],
    forearm: [-0.3, -0.7, -1.1, 1.15, 1.3, 0.85, 0.3, 0],
    lean: [0, -1, -3, 4, 6, 4, 1, 0],
    impact: 3,
  },
  gather: {
    arm: [0, -0.48, -1.1, -1.65, -1.2, 0.35, 0.2, 0],
    forearm: [0, -0.8, -1.8, -2.25, -1.5, 0.65, 0.3, 0],
    lean: [0, -1, -2, -3, -1, 5, 2, 0],
    impact: 5,
  },
  water: {
    arm: [0, 0.16, 0.28, 0.4, 0.45, 0.4, 0.22, 0],
    forearm: [0, 0.25, 0.5, 0.65, 0.7, 0.65, 0.35, 0],
    lean: [0, 1, 2, 3, 3, 3, 1, 0],
    impact: 3,
  },
  plant: {
    arm: [0, 0.12, 0.3, 0.45, 0.6, 0.5, 0.24, 0],
    forearm: [0, 0.25, 0.65, 0.8, 1.05, 0.85, 0.3, 0],
    lean: [0, 1, 2, 4, 4, 3, 1, 0],
    impact: 4,
  },
  cast: {
    arm: [0, -0.25, -0.6, -0.8, 0.3, 0.55, 0.25, 0],
    forearm: [0, -0.5, -1, -1.1, 0.7, 0.9, 0.4, 0],
    lean: [0, -1, -2, -2, 2, 3, 1, 0],
    impact: 4,
  },
  dash: {
    arm: [0, -0.4, -0.6, -0.65, -0.65, -0.5, -0.2, 0],
    forearm: [0, -0.5, -0.8, -0.9, -0.9, -0.7, -0.3, 0],
    lean: [0, 8, 14, 16, 16, 12, 5, 0],
    impact: 2,
  },
} as const;
export const CROP_ART: Record<CropId, string> = {
  pumpkin: "pumpkin",
  coconut: "coconut",
  wintermelon: "wintermelon",
  banana: "banana",
  moonherb: "moonherb",
  carrot: "carrot",
  chili: "chili",
};
export const ENEMY_ART: Record<EnemyKind, string> = {
  mite: "mite",
  brute: "brute",
  spitter: "spitter",
  sentinel: "sentinel",
  moth: "moth",
};

export function animate(
  actor: Animated,
  clip: NonNullable<Animated["animation"]>["clip"],
): void {
  actor.motion=undefined;
  actor.animation = {
    clip,
    age: 0,
    duration: CLIPS[clip].frames / CLIPS[clip].fps,
  };
}
export function tickAnimation(
  actor: Animated,
  dt: number,
  velocity?: Vec2,
): void {
  if (actor.animation) {
    actor.animation.age += dt;
    if (actor.animation.age >= actor.animation.duration)
      actor.animation = undefined;
  }
  if(actor.motion){actor.motion.age+=dt;if(actor.motion.age>=actor.motion.duration)actor.motion=undefined;}
  if (velocity)
    actor.stride =
      (actor.stride ?? 0) + Math.hypot(velocity.x, velocity.y) * dt;
}
export function frameAt(clip: Clip, seconds: number): number {
  const c = CLIPS[clip],
    f = Math.max(0, Math.floor(seconds * c.fps));
  return c.loop ? f % c.frames : Math.min(c.frames - 1, f);
}
export function animationFrame(
  actor: Animated & { velocity?: Vec2 },
  time: number,
  environment = false,
): { clip: Clip; frame: number } {
  if (actor.animation)
    return {
      clip: actor.animation.clip,
      frame: frameAt(actor.animation.clip, actor.animation.age),
    };
  const walking =
    actor.velocity && Math.hypot(actor.velocity.x, actor.velocity.y) > 4;
  const clip = environment ? "wind" : walking ? "walk" : "idle";
  return {
    clip,
    frame: frameAt(clip, walking ? (actor.stride ?? 0) / 105 : time),
  };
}

/** 与离线烘焙共用姿态函数。骨长固定，近/远肢体命名不随朝向交换。 */
export function pose(
  clip: Clip,
  frame: number,
): Record<string, [number, number]> {
  const t = (frame / CLIPS[clip].frames) * Math.PI * 2,
    swing = clip === "walk" ? Math.cos(t) * 0.75 : 0;
  const action =
    clip in ACTION_PHASES
      ? ACTION_PHASES[clip as keyof typeof ACTION_PHASES]
      : null;
  const index = Math.min(7, Math.max(0, frame));
  const lean =
    clip === "hurt"
      ? -Math.sin((frame / 7) * Math.PI) * 9
      : (action?.lean[index] ?? 0);
  const crouch = clip === "plant" ? [0, 2, 5, 8, 8, 6, 3, 0][index] : 0;
  const bob =
    (clip === "walk" ? Math.sin(t * 2) * 1.2 : Math.sin(t) * 0.7) + crouch;
  const hip: [number, number] = [80 + lean, 111 + bob],
    shoulder: [number, number] = [80 + lean, 72 + bob];
  const extend = (
    base: [number, number],
    a: number,
    len: number,
  ): [number, number] => [
    base[0] + Math.sin(a) * len,
    base[1] + Math.cos(a) * len,
  ];
  const nearKnee = extend(hip, swing, 17),
    farKnee = extend(hip, -swing, 17);
  const nearAnkle = extend(nearKnee, swing * 0.55, 17),
    farAnkle = extend(farKnee, -swing * 0.55, 17);
  const arm = action?.arm[index] ?? 0;
  const nearElbow = extend(shoulder, -swing * 0.8 + arm, 18),
    farElbow = extend(shoulder, swing * 0.8 - arm * 0.25, 18);
  return {
    head: [80 + lean, 45 + bob],
    shoulder,
    hip,
    near_shoulder: shoulder,
    far_shoulder: shoulder,
    near_elbow: nearElbow,
    far_elbow: farElbow,
    near_wrist: extend(
      nearElbow,
      -swing * 0.8 + (action?.forearm[index] ?? 0),
      18,
    ),
    far_wrist: extend(farElbow, swing * 0.8 - arm * 0.25, 18),
    near_knee: nearKnee,
    far_knee: farKnee,
    near_ankle: nearAnkle,
    far_ankle: farAnkle,
    near_toe: [nearAnkle[0] + 6, nearAnkle[1]],
    far_toe: [farAnkle[0] + 6, farAnkle[1]],
  };
}
